import { useQuery } from '@tanstack/react-query';

import { QuestionnaireService } from '../../../js/api';
import { providerQueryKeys } from '../../../js/app/query-keys-factories/providers';
import { DrawerField } from './drawer-components';
import { Spinner } from './spinner';

export const QUESTION_DEFINITIONS = [
  {
    key: 'licensureChallenges',
    section: 'Licensure',
    question:
      'Have you faced any challenges to your licensure, registration, or certification, or have you been investigated, sanctioned, reprimanded, or resigned under investigation?',
  },
  {
    key: 'liabilityIssues',
    section: 'Professional Liability & Claims History',
    question:
      'Have you experienced any issues with your professional liability coverage, such as cancellations, restrictions, surcharges, or high-risk ratings based on your individual liability history?',
  },
  {
    key: 'liabilityActions',
    section: 'Professional Liability & Claims History',
    question:
      'Have you ever had any professional liability actions (pending, settled, arbitrated, mediated or litigated)?',
  },
  {
    key: 'backgroundCheck',
    section: 'Background',
    question:
      'Is there anything you need to disclose that could be found on a background check which could have a negative impact on your application(s) for hospital privileges?',
  },
  {
    key: 'substanceUse',
    section: 'Job Performance',
    question:
      'Do you currently use any substances, legal or illegal, that could impair your ability to perform your job safely and effectively, or do you have any reason to believe you would pose a risk to patient safety or well-being?',
  },
] as const;

const SECTIONS = [
  'Licensure',
  'Professional Liability & Claims History',
  'Background',
  'Job Performance',
] as const;

type ProviderQuestionnaireOrgViewProps = {
  providerId: string;
};

export function ProviderQuestionnaireOrgView({ providerId }: ProviderQuestionnaireOrgViewProps) {
  const { data, isLoading, isError } = useQuery({
    queryKey: providerQueryKeys.questionnaire(providerId),
    queryFn: () => QuestionnaireService.questionnaireRetrieve({ providerId }),
    enabled: !!providerId,
  });

  if (isLoading) {
    return (
      <div className="tw:flex tw:items-center tw:justify-center tw:py-12">
        <Spinner aria-label="Loading questionnaire" variant="zinc" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="tw:rounded-lg tw:bg-zinc-100 tw:dark:bg-zinc-800 tw:p-4 tw:text-zinc-600 tw:dark:text-zinc-400">
        Unable to load questionnaire.
      </div>
    );
  }

  const questionnaireData = data?.data;
  const hasData =
    questionnaireData &&
    typeof questionnaireData === 'object' &&
    Object.keys(questionnaireData).length > 0;

  if (!hasData) {
    return (
      <div className="tw:rounded-lg tw:bg-zinc-100 tw:dark:bg-zinc-800 tw:p-4 tw:text-zinc-600 tw:dark:text-zinc-400">
        No questionnaire on file.
      </div>
    );
  }

  return (
    <div className="tw:space-y-6">
      {SECTIONS.map((sectionTitle) => {
        const sectionQuestions = QUESTION_DEFINITIONS.filter((q) => q.section === sectionTitle);
        return (
          <section key={sectionTitle} className="tw:space-y-4">
            {/* <DrawerSectionHeader
              text={sectionTitle}
              icon={
                <i className="fas fa-clipboard-question tw:text-brand-green-0 tw:dark:text-brand-green-1 fa-fw" />
              }
            /> */}
            <div className="tw:space-y-8">
              {sectionQuestions.map(({ key, question }) => {
                const item = questionnaireData[key];
                const value = item?.value;
                const comment = item?.comment;
                const answerText = value === true ? 'YES' : value === false ? 'NO' : '--';

                return (
                  <div key={key} className="tw:space-y-1">
                    <div className="tw:text-zinc-700 tw:dark:text-zinc-300 tw:text-sm">
                      {question}
                    </div>
                    <div className="tw:grid tw:grid-cols-[auto_1fr] tw:gap-x-6 tw:gap-y-2 tw:text-sm">
                      <DrawerField label="Answer" value={answerText} />
                    </div>
                    {value === true && comment && (
                      <div className="tw:mt-2 tw:ml-5.75">
                        <div className="tw:text-zinc-400 tw:text-sm">Comment:</div>
                        <div className="tw:text-zinc-700 tw:dark:text-zinc-300 tw:whitespace-pre-line tw:rounded tw:bg-zinc-50 tw:dark:bg-zinc-800/50 tw:p-3 tw:mt-1">
                          {comment}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
}
